import { Dialog } from '@headlessui/react';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { Modal } from '@/components/common/modal';
import { Table } from '@/components/common/table';
import { Button, ButtonVariant } from '@/components/form/button';
import { SearchInput } from '@/components/form/search-input';
import { useAuth } from '@/features/auth';
import { getFermentations } from '@/features/fermentation/services/fermentations';
import { FermentationQueryKeys } from '@/features/fermentation/services/query-keys';
import { IFermentation } from '@/features/fermentation/types/fermentation';
import { useInfinitePagination } from '@/hooks/use-infinite-pagination';
import { getAdminGroup } from '@/utils/get-admin-group';

import { linkBatchFromFermentation } from '../../services/batches';
import { Method, State } from '../../types';
import { selectionColumns } from './columns';

interface IProps {
  /** based on the state, modal will be rendered */
  isOpen: boolean;

  /** toggle modal open and close */
  toggle: () => void;

  /** The unique identifier of the batch to be linked */
  batchId: string;

  onSuccess?: (fermentationId: string) => void;
}

export const LinkFermentationModal: React.FC<IProps> = ({
  batchId,
  onSuccess,
  isOpen,
  toggle,
}) => {
  const { t } = useTranslation('translation', { useSuspense: false });
  // const { state } = useFermentationAction();

  // determine analysis method based on signed in method
  const { user } = useAuth();
  const analysisMethod = useMemo(() => {
    if (user?.signedInMethod === 'wine') return Method.WineConzViab;
    if (user?.signedInMethod === 'beer') return Method.Yeast2ConzViab;
    if (user?.signedInMethod === 'ethanol') return Method.BioEthConzViab;
    return Method.WineConz;
  }, [user]);

  // search by name filter
  const [name, setName] = useState('');

  // get admin role
  const group = useMemo(() => getAdminGroup(user?.roles), [user]);

  // selected fermentation id
  const [selectedFermentationId, setSelectedFermentationId] = useState<string | null>(
    null,
  );

  const linkMutation = useMutation({
    mutationFn: ({
      batchId,
      fermentationId,
    }: {
      batchId: string;
      fermentationId: string;
    }) => linkBatchFromFermentation({ batchId, fermentationId }),
    onSuccess: () => {
      toast.success(t('fermentations.link.successMessage'));
      onSuccess && onSuccess(selectedFermentationId!);
    },
    onError: async () => {
      toast.error(t('fermentations.link.errorMessage'));
    },
    onSettled: () => {
      toggle();
    },
  });

  const query = useInfiniteQuery({
    queryKey: [FermentationQueryKeys.GetAllFermentations, name],
    queryFn: ({ pageParam }) =>
      getFermentations({
        states: [
          State.FermentationOngoing,
          State.FermentationComplete,
          State.UserFermentationCompleted,
        ],
        page: pageParam,
        method: analysisMethod,
        name,
        group,
      }),
  });
  const { data, state, fetchNextPage } = useInfinitePagination<IFermentation>({ query });

  // apply search filter
  const onSearch = (search: string) => {
    setName(search);
    if (search === '') query.refetch(); // refetch in case user clear out all
  };

  const handleSubmit = () => {
    if (!selectedFermentationId) {
      toast.error(t('fermentations.link.noFermentationSelected'));
      return;
    }
    linkMutation.mutate({ batchId, fermentationId: selectedFermentationId });
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      title={t('fermentations.link.title')}
      conatinerClassnames="w-full md:w-5/6 lg:w-2/3 xl:w-2/3 2xl:w-2/3"
    >
      <div className="flex flex-col gap-5">
        <Dialog.Description>{t('fermentations.link.description')}</Dialog.Description>
        <div className="pt-2 pb-8">
          <SearchInput onSearch={onSearch} isLoading={query.isFetching} />
        </div>
      </div>
      <div className="h-96 overflow-y-auto">
        <Table<IFermentation>
          data={data?.length ? data : []}
          columns={selectionColumns}
          fetchNextPage={fetchNextPage}
          state={state}
          onRowClick={(data: IFermentation) => {
            setSelectedFermentationId(data.id);
          }}
          bulkActions={false}
        />
      </div>
      <div className="flex justify-end gap-4 pt-5">
        <Button
          variant={ButtonVariant.Secondary}
          onClick={() => toggle()}
          disabled={linkMutation.isLoading}
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          isLoading={linkMutation.isLoading}
          disabled={!selectedFermentationId}
        >
          {t('fermentations.link.confirmButton')}
        </Button>
      </div>
    </Modal>
  );
};
